import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Crown, Globe2, Loader2, Lock, Search, Trash2, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  ResourceAccessRole,
  ResourceAccessType,
  useAddResourcePermission,
  useRemoveResourcePermission,
  useResourcePermissionsList,
  useUpdateResourcePermission,
} from '@/hooks/useResourceAccess';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ResourceAccessSectionProps {
  type: ResourceAccessType;
  resourceId?: string;
  ownerId?: string;
  scopeType?: string;
  projectId?: string | null;
  canManage: boolean;
}

export const ResourceAccessSection = ({ type, resourceId, ownerId, scopeType, projectId, canManage }: ResourceAccessSectionProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteRole, setInviteRole] = useState<ResourceAccessRole>('viewer');
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { data: permissions = [], isLoading } = useResourcePermissionsList(type, resourceId);
  const addPermission = useAddResourcePermission(type);
  const updatePermission = useUpdateResourcePermission(type);
  const removePermission = useRemoveResourcePermission(type);

  const { data: owner } = useQuery({
    queryKey: ['resource-owner', ownerId],
    enabled: !!ownerId,
    queryFn: async () => {
      const { data, error } = await supabase.from('profiles').select('id, nome, email, avatar_url').eq('id', ownerId!).single();
      if (error) throw error;
      return data;
    },
  });

  const { data: searchResults = [] } = useQuery({
    queryKey: ['resource-access-profile-search', type, resourceId, searchQuery],
    enabled: canManage && searchQuery.length >= 2,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, email, avatar_url')
        .or(`email.ilike.%${searchQuery}%,nome.ilike.%${searchQuery}%`)
        .limit(10);
      if (error) throw error;
      const blockedIds = new Set([ownerId, ...permissions.map(p => p.user_id)].filter(Boolean));
      return (data ?? []).filter(p => !blockedIds.has(p.id));
    },
  });

  const inheritedLabel = useMemo(() => {
    if (projectId) return 'Herdado do projeto atual';
    if (scopeType === 'global') return 'Todos no Espaço Caldeira podem ver e usar';
    if (scopeType === 'team') return 'Herdado dos membros do time';
    return 'Privado por padrão, exceto pessoas adicionadas abaixo';
  }, [projectId, scopeType]);

  const handleInvite = (userId: string) => {
    if (!resourceId) return;
    addPermission.mutate({ resourceId, userId, role: inviteRole }, {
      onSuccess: () => { setSearchQuery(''); toast.success('Acesso concedido'); },
      onError: (err: Error) => toast.error(`Erro ao conceder acesso: ${err.message}`),
    });
  };

  if (!resourceId) return null;

  return (
    <section className="space-y-4 border-t border-color pt-6">
      <div>
        <h2 className="text-sm font-semibold text-primary mb-1 flex items-center gap-2"><Users className="w-4 h-4" />Acesso</h2>
        <p className="text-xs text-tertiary">Gerencie quem pode ver, usar ou editar este {type === 'prompt' ? 'prompt' : 'agente'}.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-color rounded-md px-3 py-2.5 flex items-center gap-3">
          <Avatar profile={owner} />
          <div className="flex-1 min-w-0"><p className="text-sm text-primary truncate">{owner?.nome ?? 'Carregando...'}</p><p className="text-xs text-tertiary truncate">{owner?.email}</p></div>
          <span className="text-xs text-tertiary px-2 py-1 bg-secondary rounded flex items-center gap-1"><Crown className="w-3 h-3" />Dono</span>
        </div>
        <div className="border border-color rounded-md px-3 py-2.5 flex items-center gap-3">
          {scopeType === 'personal' ? <Lock className="w-8 h-8 text-tertiary p-1.5 bg-secondary rounded-full" /> : <Globe2 className="w-8 h-8 text-tertiary p-1.5 bg-secondary rounded-full" />}
          <div className="flex-1 min-w-0"><p className="text-sm text-primary truncate">Acesso do ambiente</p><p className="text-xs text-tertiary truncate">{inheritedLabel}</p></div>
        </div>
      </div>

      {canManage && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" /><input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar por email ou nome..." className="w-full h-10 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary" /></div>
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value as ResourceAccessRole)} className="px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"><option value="viewer">Visualizador</option><option value="editor">Editor</option></select>
          </div>
          {searchResults.length > 0 && <div className="border border-color rounded-md max-h-40 overflow-y-auto">{searchResults.map(p => <button key={p.id} type="button" onClick={() => handleInvite(p.id)} disabled={addPermission.isPending} className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-hover transition-colors"><Avatar profile={p} size="sm" /><div className="flex-1 text-left min-w-0"><p className="text-primary truncate">{p.nome ?? 'Sem nome'}</p><p className="text-xs text-tertiary truncate">{p.email}</p></div><UserPlus className="w-4 h-4 text-tertiary flex-shrink-0" /></button>)}</div>}
          {searchQuery.length >= 2 && searchResults.length === 0 && <p className="text-xs text-tertiary px-1">Nenhum usuário encontrado.</p>}
        </div>
      )}

      <div className="border border-color rounded-md divide-y divide-border">
        {isLoading ? <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-tertiary" /></div> : permissions.length > 0 ? permissions.map(permission => (
          <div key={permission.id} className="flex items-center gap-3 px-3 py-2.5">
            <Avatar profile={permission.profile} />
            <div className="flex-1 min-w-0"><p className="text-sm text-primary truncate">{permission.profile.nome ?? 'Sem nome'}</p><p className="text-xs text-tertiary truncate">{permission.profile.email}</p></div>
            {canManage ? <select value={permission.role} onChange={e => updatePermission.mutate({ permissionId: permission.id, resourceId, role: e.target.value as ResourceAccessRole }, { onSuccess: () => toast.success('Permissão atualizada'), onError: (err: Error) => toast.error(`Erro: ${err.message}`) })} className="px-2 py-1 rounded border border-color bg-surface text-xs text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"><option value="viewer">Visualizador</option><option value="editor">Editor</option></select> : <span className="text-xs text-tertiary px-2 py-1">{permission.role === 'editor' ? 'Editor' : 'Visualizador'}</span>}
            {canManage && <button type="button" onClick={() => setRemovingId(permission.id)} className="p-1 hover:bg-hover rounded text-tertiary hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
          </div>
        )) : <p className="text-sm text-tertiary text-center py-6">Nenhuma pessoa com acesso individual.</p>}
      </div>

      <AlertDialog open={!!removingId} onOpenChange={() => setRemovingId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Revogar acesso</AlertDialogTitle><AlertDialogDescription>Esta pessoa perderá o acesso individual. Ela ainda poderá acessar se tiver acesso pelo ambiente.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => removingId && removePermission.mutate({ permissionId: removingId, resourceId }, { onSuccess: () => { setRemovingId(null); toast.success('Acesso revogado'); }, onError: (err: Error) => { setRemovingId(null); toast.error(`Erro: ${err.message}`); } })} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Revogar</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </section>
  );
};

const Avatar = ({ profile, size = 'md' }: { profile?: { nome: string | null; email: string; avatar_url: string | null } | null; size?: 'sm' | 'md' }) => {
  const className = size === 'sm' ? 'w-7 h-7' : 'w-8 h-8';
  return <div className={`${className} rounded-full bg-secondary flex items-center justify-center text-xs font-medium text-primary flex-shrink-0`}>{profile?.avatar_url ? <img src={profile.avatar_url} className={`${className} rounded-full object-cover`} alt="" /> : ((profile?.nome?.[0] ?? profile?.email?.[0] ?? '?').toUpperCase())}</div>;
};